import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  PRODUCT_PROGRESS,
  clampProgress,
  type ProductProgressKey,
} from '@/config/productProgress';

type ProductProgressMap = Record<ProductProgressKey, number>;

interface ProductProgressContextValue {
  progress: ProductProgressMap;
  refreshProgress: () => Promise<void>;
}

const ProductProgressContext = createContext<ProductProgressContextValue>({
  progress: PRODUCT_PROGRESS,
  refreshProgress: async () => undefined,
});

function normalizeProgressMap(value: unknown): ProductProgressMap {
  const raw = value && typeof value === 'object' ? value as Partial<Record<ProductProgressKey, unknown>> : {};

  return {
    'defter-kasa': clampProgress(raw['defter-kasa'] ?? PRODUCT_PROGRESS['defter-kasa']),
    'kozmik-gunluk': clampProgress(raw['kozmik-gunluk'] ?? PRODUCT_PROGRESS['kozmik-gunluk']),
    'anilar-muzesi': clampProgress(raw['anilar-muzesi'] ?? PRODUCT_PROGRESS['anilar-muzesi']),
  };
}

export function ProductProgressProvider({ children }: { children: ReactNode }) {
  const [progress, setProgress] = useState<ProductProgressMap>(PRODUCT_PROGRESS);

  const refreshProgress = async () => {
    try {
      const response = await fetch('/api/progress', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) return;

      const data = await response.json().catch(() => null) as { progress?: unknown } | null;
      if (data?.progress) {
        setProgress(normalizeProgressMap(data.progress));
      }
    } catch {
      // Keep the bundled defaults when the runtime API is unavailable.
    }
  };

  useEffect(() => {
    void refreshProgress();
  }, []);

  const value = useMemo(() => ({ progress, refreshProgress }), [progress]);

  return (
    <ProductProgressContext.Provider value={value}>
      {children}
    </ProductProgressContext.Provider>
  );
}

export function useProductProgress(key: ProductProgressKey): number {
  return useContext(ProductProgressContext).progress[key];
}

export function useProductProgressMap(): ProductProgressContextValue {
  return useContext(ProductProgressContext);
}
